import type { ContributorTypeValue as Contributor_Type_Enum } from '@risksmart-app/domain/src/types/consts/contributor-type';
import type { ReactNode } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { Filter } from 'src/components/user-search-preferences/useGroupAndUserOptions';
import type { AncestorContributorPart } from 'src/contracts/querySelections';
import type { useGetUsers } from 'src/hooks/queries/user/useGetUsers';
import type { useGetUserGroups } from 'src/hooks/queries/user-group/useGetUserGroups';

import ControlledGroupAndUserMultiSelect from '../controlled-group-and-user-multi-select';
import type { ControlledBaseProps } from '../types';
import { ContributorTokens } from './ContributorTokens';

type GetUsersData = NonNullable<ReturnType<typeof useGetUsers>['data']>;

type GetUserGroupsRow = NonNullable<
  ReturnType<typeof useGetUserGroups>['data']
>['user_group'][number];

interface Props<T extends FieldValues> extends ControlledBaseProps<T> {
  disabled?: boolean;
  constraintText?: ReactNode;
  inheritedContributorsName: FieldPath<T>;
  includeGroups: boolean;
  testId: string;
  contributorType: Contributor_Type_Enum;
  disabledOptions?: { userId: string; reason: string }[];
  userFilter?: Filter<GetUsersData['user'][number]>;
  groupFilter?: Filter<GetUserGroupsRow>;
}

const createContributorLookup = (
  ancestorsContributors: AncestorContributorPart[]
): Set<string> => {
  const items: Set<string> = new Set<string>();
  ancestorsContributors.forEach((dataItem) => {
    if (dataItem.UserGroupId) {
      items.add(dataItem.UserGroupId);
    } else {
      items.add(dataItem.UserId!);
    }
  });

  return items;
};

export const ControlledGroupAndUserContributorMultiSelect = <
  T extends FieldValues,
>({
  ...props
}: Props<T>) => {
  const { disabled, inheritedContributorsName } = props;

  const { watch } = useFormContext<T>();
  const inheritedContributors = watch(
    inheritedContributorsName
  ) as AncestorContributorPart[];

  const ancestorsContributors =
    inheritedContributors?.filter(
      (ic) =>
        ic.ContributorType === props.contributorType && ic.AncestorId !== ic.Id
    ) || [];

  const inheritedContributorLookup = createContributorLookup(
    ancestorsContributors
  );

  return (
    <ControlledGroupAndUserMultiSelect
      {...props}
      showInheritedContributorsToggle={true}
      customTokenRender={(
        tokenOptions,
        actions,
        users,
        userGroups,
        showInheritedContributors
      ) => {
        return (
          <ContributorTokens
            showInheritedContributors={showInheritedContributors}
            disabled={!!disabled}
            onRemoveToken={actions.removeToken}
            tokenOptions={tokenOptions}
            inheritedContributorLookup={inheritedContributorLookup}
            users={users}
            userGroups={userGroups}
            disabledUsers={props.disabledOptions}
          />
        );
      }}
    />
  );
};
